import { observable, action, set, extendObservable, remove, toJS, computed } from "mobx";
import Immersive3dTour from "./Immersive3dTour/Immersive3dTour";
import Address from "./Address";
import OpenHouse from "./OpenHouse";
import Customer from "./Customer";
import CustomerDefaults from "./CustomerDefaults";
import Model from "./Model";
import TourAcl from "./Tour/TourAcl";
import TourClientPanelOptions from "./Tour/TourClientPanelOptions";
import TourCheckoutPageLinks from "./Tour/TourCheckoutPageLinks";
import TourPublishStatus from "~/constants/Tour/TourPublishStatus";

class Tour extends Model {
  /**
   * @property {number|null}
   */
  @observable tourId = null;

  /**
   * @property {number|null}
   */
  @observable audioId = null;

  /**
   * @readonly
   * @property {string|null}
   */
  @observable brandedFlyerPDFUrl = null;

  /**
   * @readonly
   * @property {string|null}
   */
  @observable clientFacingStatus = null;

  /**
   * @readonly
   * @property {string|null}
   */
  @observable clientFacingStatusDescription = null;

  /**
   * @readonly
   * @property {string|null}
   */
  @observable clientFacingStatusDescriptionSimpler = null;

  /**
   * @property {boolean}
   */
  @observable isActive = false;

  /**
   * @property {boolean}
   */
  @observable isArchived = false;

  /**
   * @property {number|null}
   */
  @observable creationDts = null;

  /**
   * @property {number|null}
   */
  @observable deletedDts = null;

  /**
   * @property {Immersive3dTour|null}
   */
  @observable immersive3dTour = null;

  /**
   * @property {string|null}
   */
  @observable posterThumbnailLarge = null;

  /**
   * @property {string|null}
   */
  @observable banner = null;

  /**
   * @property {Address|object}
   */
  @observable address = {};

  /**
   * @property {string|null}
   */
  @observable title = null;

  /**
   * @property {string|null}
   */
  @observable listingInformation = null;

  /**
   * @property {object|null}
   */
  @observable mlsService = null;

  /**
   * @property {number|null}
   */
  @observable mlsNumberId = null;

  /**
   * @property {number|null}
   */
  @observable mlsNumber = null;

  /**
   * @property {object|null}
   */
  @observable coCustomer = null;

  /**
   * @property {string}
   */
  @observable pocketWebsiteDesignSkinId = "";

  /**
   * @property {string|null}
   */
  @observable propertyPrice = null;

  /**
   * @property {number|null}
   */
  @observable propertyNumBedrooms = null;

  /**
   * @property {number|null}
   */
  @observable propertyNumBathrooms = null;

  /**
   * @property {number|null}
   */
  @observable propertySize = null;

  /**
   * @property {number|null}
   */
  @observable propertyLotSize = null;

  /**
   * @property {number|null}
   */
  @observable propertyYearBuilt = null;

  /**
   * @property {string}
   */
  @observable propertyType = null;

  /**
   * @property {string}
   */
  @observable propertyStatus = null;

  /**
   * @property {boolean}
   */
  @observable published = false;

  /**
   * @property {object}
   */
  @observable openHouse = {};

  /**
   * @property {string|null}
   */
  @observable heading1 = null;

  /**
   * @property {string|null}
   */
  @observable heading2 = null;

  /**
   * @property {string|null}
   */
  @observable heading3 = null;

  /**
   * @property {boolean}
   */
  @observable supportsHeading3 = false;

  /**
   * @property {string|null}
   */
  @observable brandedTourURL = null;

  /**
   * @property {string|null}
   */
  @observable idxTourURL = null;

  /**
   * @property {string|null}
   */
  @observable brandedPocketWebsiteURL = null;

  /**
   * @property {string|null}
   */
  @observable unbrandedPocketWebsiteURL = null;

  /**
   * @property {string|null}
   */
  @observable immersive3dTourURL = null;

  /**
   * @property {string|null}
   */
  @observable galleryUrl = null;

  /**
   * @property {string|null}
   */
  @observable tourUrl = null;

  /**
   * @property {string|null}
   */
  @observable brandedTourUrl = null;

  /**
   * @property {string|null}
   */
  @observable idxTourUrl = null;

  /**
   * @property {boolean|null}
   */
  @observable shouldDisplayUnbrandedFlyerUrl = false;

  /**
   * @property {string|null}
   */
  @observable unbrandedFlyerUrlForMlsSettings = null;

  /**
   * @property {string|null}
   */
  @observable showMap = null;

  /**
   * @property {string|null}
   */
  @observable hasShowOtherTours = null;

  /**
   * @property {string|null}
   */
  @observable hasSendToFriend = null;

  /**
   * @property {string|null}
   */
  @observable showPrintableFlyer = null;

  /**
   * @property {string|null}
   */
  @observable customFlyerUrlBranded = null;

  /**
   * @property {string|null}
   */
  @observable customFlyerUrlUnbranded = null;

  /**
   * @property {boolean|null}
   */
  @observable isPocketWebsite = null;

  /**
   * @property {string|null}
   */
  @observable showNeighborhood = null;

  /**
   * @property {string|null}
   */
  @observable customNeighborhoodLabel = null;

  /**
   * @property {string|null}
   */
  @observable customNeighborhoodUrl = null;

  /**
   * @property {boolean|null}
   */
  @observable customNeighborhoodOpenInNewWindow = null;

  /**
   * @property {string|null}
   */
  @observable customLink1Label = null;

  /**
   * @property {string|null}
   */
  @observable customLink1UrlBranded = null;

  /**
   * @property {string|null}
   */
  @observable customLink1UrlUnbranded = null;

  /**
   * @property {boolean|null}
   */
  @observable customLink1OpenInNewWindow = null;

  /**
   * @property {string|null}
   */
  @observable customLink2Label = null;

  /**
   * @property {string|null}
   */
  @observable customLink2UrlBranded = null;

  /**
   * @property {string|null}
   */
  @observable customLink2UrlUnbranded = null;

  /**
   * @property {boolean|null}
   */
  @observable customLink2OpenInNewWindow = null;

  /**
   * @property {string|null}
   */
  @observable matterportLabel = null;

  /**
   * @property {string|null}
   */
  @observable matterportUrlBranded = null;

  /**
   * @property {string|null}
   */
  @observable matterportUrlUnbranded = null;

  /**
   * @property {number|null}
   */
  @observable slideshowTime = null;

  /**
   * @property {string|null}
   */
  @observable autoplay = null;

  /**
   * @property {string|null}
   */
  @observable videoSlideshowMode = null;

  /**
   * @property {string|null}
   */
  @observable videoSlideshowProgramMode = null;

  /**
   * @property {string|null}
   */
  @observable panningDuration = null;

  /**
   * @property {string|null}
   */
  @observable panningTime = null;

  /**
   * @property {number}
   */
  @observable sendTourStatsEveryNDays = null;

  /**
   * @property {boolean}
   */
  @observable sendTourStatsToCustomer = false;

  /**
   * @property {boolean}
   */
  @observable canDisplayAsPocketWebsite = false;

  /**
   * @property {boolean}
   */
  @observable hasReadiedImmersive3dTour = false;

  /**
   * @property {boolean}
   */
  @observable isUiMeet3dActive = false;

  /**
   * @property {string[]}
   */
  @observable statsEmailCcs = [];

  /**
   * @property {Customer|null}
   */
  @observable customer = null;

  /**
   * @property {number|null}
   */
  @observable customUploadedAudioId = null;

  /**
   * @property {number|null}
   */
  @observable uiMeet3dLevel = null;

  /**
   * @property {string|null}
   */
  @observable shootDtsAsString = null;

  /**
   * @property {string|null}
   */
  @observable shootTimezone = null;

  /**
   * @property {string|null}
   */
  @observable shootSize = null;

  /**
   * @property {number|null}
   */
  @observable publishStatusCode = null;

  /**
   * @property {CustomerDefaults}
   */
  @observable customerDefaults = new CustomerDefaults();

  /**
   * @property {TourAcl}
   */
  @observable acl = new TourAcl();

  /**
   * @property {TourClientPanelOptions}
   */
  @observable clientPanelOptions = new TourClientPanelOptions();

  /**
   * @property {TourCheckoutPageLinks}
   */
  @observable checkoutPageLinks = new TourCheckoutPageLinks();

  /**
   * Set new data to our model
   * @override
   * @param {object} data
   */
  @action
  setData(data) {
    const {
      address,
      customer,
      customerDefaults,
      openHouse,
      immersive3dTour,
      acl,
      clientPanelOptions,
      checkoutPageLinks,
      ...rest
    } = data;
    set(this, rest);

    if (address) {
      this.address = new Address(address);
    }

    if (customer) {
      this.customer = new Customer(customer);
    }

    if (customerDefaults) {
      this.customerDefaults = new CustomerDefaults(customerDefaults);
    }

    if (openHouse) {
      openHouse.forEach((oh, i) => {
        extendObservable(this.openHouse, { [`${i}`]: new OpenHouse(oh, this, "openHouse") });
      });
    } else {
      extendObservable(this.openHouse, { [`0`]: new OpenHouse({}, this, "openHouse") });
    }

    if (immersive3dTour) {
      this.immersive3dTour = new Immersive3dTour(immersive3dTour);
    }

    if (acl) {
      this.acl = new TourAcl(acl);
    }

    if (clientPanelOptions) {
      this.clientPanelOptions = new TourClientPanelOptions(clientPanelOptions);
    }

    if (checkoutPageLinks) {
      this.checkoutPageLinks = new TourCheckoutPageLinks(checkoutPageLinks);
    }
  }

  @action
  addOpenHouse() {
    const nextIndex = Object.keys(this.openHouse).length;
    extendObservable(this.openHouse, { [`${nextIndex}`]: new OpenHouse({}, this, "openHouse") });
  }

  @action
  deleteOpenHouse(index) {
    const tmpArray = [];
    const keys = Object.keys(this.openHouse);
    keys.forEach(i => {
      if (i === index) {
        return;
      }
      tmpArray.push(this.openHouse[i]);
    });
    remove(this.openHouse, keys[keys.length - 1]);
    tmpArray.forEach((openHouse, i) => {
      set(this.openHouse, { [`${i}`]: openHouse });
    });
  }

  @action
  addStatsEmail() {
    this.statsEmailCcs.push("");
  }

  @action
  deleteStatsEmail(email) {
    this.statsEmailCcs.remove(email);
  }

  /**
   * Return an object
   * {@returns {object}}
   */
  @action
  getDirtyData() {
    const payload = super.getDirtyData();

    // Every time we send the mlsServiceId
    // We need to send the mlsNumber, and vice versa
    if (typeof payload.mlsServiceId !== "undefined" && typeof payload.mlsNumber === "undefined") {
      payload.mlsNumber = toJS(this.mlsNumber);
    }

    if (typeof payload.mlsNumber !== "undefined" && typeof payload.mlsServiceId === "undefined") {
      payload.mlsServiceId = toJS(this.mlsServiceId);
    }

    return payload;
  }

  @computed
  get okToPubliclyDisplayAddress() {
    if (typeof this.address.city === "undefined") {
      return false;
    }

    // If we can't find the word Somewhere at the city
    return this.address.city.indexOf("Somewhere") === -1;
  }

  @computed
  get uiMeet3dLevelCoalesced() {
    if (this.uiMeet3dLevel !== null) {
      return this.uiMeet3dLevel;
    }

    return this.customerDefaults.uiMeet3dLevel;
  }

  @action
  setImmersive3dTour(immersive3dTour) {
    this.immersive3dTour = immersive3dTour;
  }

  @computed
  get customerHasUrbanimmersiveGroupSpecialPricing() {
    return (
      typeof this.customer !== "undefined" &&
      typeof this.customer.hasUrbanimmersiveGroupSpecialPricing !== "undefined" &&
      this.customer.hasUrbanimmersiveGroupSpecialPricing
    );
  }

  @computed
  get isFullPublished() {
    return this.publishStatusCode === TourPublishStatus.PUBLISHED_FULLY;
  }
}

export default Tour;
